import React, { Fragment, useEffect, useState } from 'react'
import { Combobox, Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { UsersIcon } from '@heroicons/react/24/outline'
import { changeRequestStatus, selectAllUsers, usersUnloaded } from '../../../../reducers/users/user-list'
import { REQUEST_STATUSES } from '../../../../reducers/reducer-helper'
import { getAllUsers } from '../../../users/slice'
import { useDispatch, useSelector } from 'react-redux'
import {
  changeAccountUuid,
  changeMetaPrompt,
  changeName,
  changeUserPrompt,
  formInit,
  selectError,
  selectFormData,
  selectRequestStatus,
} from '../../../../reducers/meetings/types/update'
import { updateType } from './slice'
import { selectTypeDetails } from '../../../../reducers/meetings/types/details'
import { useParams } from 'react-router-dom'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function UpdateType({ open, setOpen }) {
  const dispatch = useDispatch()

  const { meetingTypeUuid } = useParams()

  const type = useSelector(selectTypeDetails)
  const users = useSelector(selectAllUsers)

  const [query, setQuery] = useState('')
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [openSearch, setOpenSearch] = useState(true)

  const formData = useSelector(selectFormData)
  const error = useSelector(selectError)
  const requestStatus = useSelector(selectRequestStatus)

  const changeNameProcess = (event) => {
    event.preventDefault()
    dispatch(changeName({ name: event.target.value }))
  }

  const changeMetaPromptProcess = (event) => {
    event.preventDefault()
    dispatch(changeMetaPrompt({ metaPrompt: event.target.value }))
  }

  const changeUserPromptProcess = (event) => {
    event.preventDefault()
    dispatch(changeUserPrompt({ userPrompt: event.target.value }))
  }

  const changeAccountUuidProcess = (value) => {
    dispatch(changeAccountUuid({ accountUuid: value }))

    const account = users.find((user) => user.uuid === value)
    setQuery(account.profile.fullName)
    setSelectedAccount(account)
  }

  const queryChangeProcess = (event) => {
    setSelectedAccount(null)
    setQuery(event.target.value)
  }

  const updateTypeProcess = (event) => {
    event.preventDefault()
    dispatch(changeRequestStatus({ status: REQUEST_STATUSES.PENDING }))
    dispatch(
      updateType({
        typeUuid: meetingTypeUuid,
        name: formData.name,
        metaPrompt: formData.metaPrompt,
        userPrompt: formData.userPrompt,
        accountUuid: formData.accountUuid,
      }),
    )
  }

  useEffect(() => {
    dispatch(changeRequestStatus({ status: REQUEST_STATUSES.PENDING }))
    const fetchUsers = dispatch(getAllUsers())

    return () => {
      dispatch(usersUnloaded())
      fetchUsers.abort()
    }
  }, [])

  useEffect(() => {
    if (type) {
      dispatch(
        formInit({
          name: type.name,
          metaPrompt: type.metaPrompt,
          userPrompt: type.userPrompt,
          accountUuid: type.accountUuid,
        }),
      )

      if (users && type.accountUuid) {
        const account = users.find((user) => user.uuid === type.accountUuid)
        setSelectedAccount(account)
        setQuery(account.profile.fullName)
      }
    }
  }, [type, users])

  const filteredPeople =
    query === '' || !users
      ? []
      : users.filter((user) => {
          return user.profile.fullName.toLowerCase().includes(query.toLowerCase())
        })

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <form
                    onSubmit={updateTypeProcess}
                    className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl"
                  >
                    <div className="flex-1">
                      {/* Header */}
                      <div className="bg-gray-50 px-4 py-6 sm:px-6">
                        <div className="flex items-start justify-between space-x-3">
                          <div className="space-y-1">
                            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
                              New meeting type
                            </Dialog.Title>
                            <p className="text-sm text-gray-500">
                              Get started by filling in the information below to create new meeting type.
                            </p>
                          </div>
                          <div className="flex h-7 items-center">
                            <button
                              type="button"
                              className="relative text-gray-400 hover:text-gray-500"
                              onClick={() => setOpen(false)}
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Divider container */}
                      <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                        <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                          <div>
                            <label
                              htmlFor="project-name"
                              className="block text-sm font-medium leading-6 text-gray-900 sm:mt-1.5"
                            >
                              Name
                            </label>
                          </div>
                          <div className="sm:col-span-2">
                            <input
                              type="text"
                              name="type-name"
                              id="type-name"
                              onChange={changeNameProcess}
                              value={formData.name}
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                          <div>
                            <label
                              htmlFor="project-description"
                              className="block text-sm font-medium leading-6 text-gray-900 sm:mt-1.5"
                            >
                              Meta prompt
                            </label>
                          </div>
                          <div className="sm:col-span-2">
                            <textarea
                              id="type-meta-prompt"
                              name="type-meta-prompt"
                              onChange={changeMetaPromptProcess}
                              value={formData.metaPrompt}
                              rows={3}
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                          <div>
                            <label
                              htmlFor="project-description"
                              className="block text-sm font-medium leading-6 text-gray-900 sm:mt-1.5"
                            >
                              User prompt
                            </label>
                          </div>
                          <div className="sm:col-span-2">
                            <textarea
                              id="type-user-prompt"
                              name="type-user-prompt"
                              onChange={changeUserPromptProcess}
                              value={formData.userPrompt}
                              rows={3}
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        </div>

                        <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                          <div>
                            <label
                              htmlFor="project-name"
                              className="block text-sm font-medium leading-6 text-gray-900 sm:mt-1.5"
                            >
                              Attached to account
                              <br />
                              <span className="text-gray-300 text-xs ">(Optional)</span>
                            </label>
                          </div>
                          <div className="sm:col-span-2">
                            <Transition.Root show={openSearch} as={Fragment} afterLeave={() => setQuery('')} appear>
                              <div className="block w-full rounded-md text-gray-900 placeholder:text-gray-400">
                                <Transition.Child
                                  as={Fragment}
                                  enter="ease-out duration-300"
                                  enterFrom="opacity-0 scale-95"
                                  enterTo="opacity-100 scale-100"
                                  leave="ease-in duration-200"
                                  leaveFrom="opacity-100 scale-100"
                                  leaveTo="opacity-0 scale-95"
                                >
                                  <Dialog.Panel className="mx-auto max-w-xl transform rounded-xl bg-white ring-1 ring-black ring-opacity-5 transition-all">
                                    <Combobox
                                      onChange={(value) => {
                                        changeAccountUuidProcess(value)
                                      }}
                                    >
                                      <input
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        placeholder="Search account..."
                                        value={query}
                                        onChange={(event) => queryChangeProcess(event)}
                                      />

                                      {filteredPeople.length > 0 && !selectedAccount && (
                                        <Combobox.Options
                                          static
                                          className="-mb-2 max-h-72 scroll-py-2 overflow-y-auto py-2 text-sm text-gray-800"
                                        >
                                          {filteredPeople.map((account) => (
                                            <Combobox.Option
                                              key={account.uuid}
                                              value={account.uuid}
                                              className={({ active }) =>
                                                classNames(
                                                  'cursor-default select-none rounded-md px-4 py-2',
                                                  active && 'bg-indigo-600 text-white',
                                                )
                                              }
                                            >
                                              <p className="text-sm">{account.profile.fullName}</p>
                                              <p className="whitespace-nowrap text-xs text-gray-400">{account.email}</p>
                                            </Combobox.Option>
                                          ))}
                                        </Combobox.Options>
                                      )}

                                      {query !== '' && filteredPeople.length === 0 && (
                                        <div className="px-4 py-14 text-center sm:px-14">
                                          <UsersIcon className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                                          <p className="mt-4 text-sm text-gray-900">
                                            No people found using that search term.
                                          </p>
                                        </div>
                                      )}
                                    </Combobox>
                                  </Dialog.Panel>
                                </Transition.Child>
                              </div>
                            </Transition.Root>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          onClick={() => setOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
